import throttle from "lodash/throttle";

import "./polyfill";

import "@/blocks/Gallery";
import "@/blocks/Hero";
import "@/blocks/LatestPublications";
import "@/blocks/LatestNews";
import "@/blocks/MobileSeparator";
import "@/blocks/NumberedList";
import "@/blocks/Paragraph";
import "@/blocks/TextImage";
import "@/blocks/UpcomingEvents";
import "@/blocks/MailingList";

import LinkList from "@/blocks/LinkList";
import Media from "@/blocks/Media";
import PersonList from "@/blocks/PersonList";

import Carousel from "@/components/Carousel";
import ContactForm from "@/components/ContactForm";
import CookieBanner from "@/components/CookieBanner";
import FileUpload from "@/components/FileUpload";
import Footer from "@/components/Footer";
import Form from "@/components/Form";
import Header from "@/components/Header";
import LayoutGrid from "@/components/LayoutGrid";
import PostList from "@/components/PostList";
import RegistrationForm from "@/components/RegistrationForm";
import SelectBox from "@/components/SelectBox";
import Share from "@/components/Share";

import "@/components/ArticleCard";
import "@/components/Button";
import "@/components/Container";
import "@/components/GenericTemplate";
import "@/components/NoResults";
import "@/components/PostBanner";
import "@/components/RadioButtons";
import "@/components/Checkboxes";
import "@/components/TitleBanner";

import ProcessPayment from "@/templates/ProcessPayment";
import Profile from "@/templates/Profile";
import "@/templates/SinglePost";

import LoginModal from "@/modals/LoginModal";
import ResetPasswordModal from "@/modals/ResetPasswordModal";
import RegistrationSuccessModal from "@/modals/RegistrationSuccessModal";
import SubscriptionFailModal from "@/modals/SubscriptionFailModal";
import SubscriptionSuccessModal from "@/modals/SubscriptionSuccessModal";

import { tablet, laptop } from "@/app/breakpoints";

// load global styles
import "@/styles/normalize.css";
import "@/styles/fonts.css";
import "@/styles/typography.css";
import "@/styles/common.css";

const MODULES = [
  Carousel,
  Header,
  FileUpload,
  Footer,
  Form,
  ContactForm,
  CookieBanner,
  LayoutGrid,
  LinkList,
  Media,
  PersonList,
  PostList,
  ProcessPayment,
  Profile,
  RegistrationForm,
  SelectBox,
  Share,
];

const MODALS = {
  login: LoginModal,
  resetPasswordModal: ResetPasswordModal,
  registrationSuccessModal: RegistrationSuccessModal,
  subscriptionFailModal: SubscriptionFailModal,
  subscriptionSuccessModal: SubscriptionSuccessModal,
};
class App {
  lastIndex = 0;

  constructor() {
    window.addEventListener("DOMContentLoaded", this.initApp);
    window.addEventListener("resize", throttle(this.onResize, 150));
    window.addEventListener("scroll", throttle(this.onScroll, 150));
  }
  initApp = () => {
    this.lastIndex = 0;
    this.currentView = this.getCurrentView();

    this.instances = [];
    this.modals = {};

    const main = document.querySelector("#main");
    if (main) {
      history.replaceState(
        {
          title: document.title,
          main: main.innerHTML,
          bodyClassAttribute: document.body.getAttribute("class"),
        },
        document.title,
        window.location.href
      );
    }

    this.initArea(document);
  };

  initArea = (parentElement) => {
    if (!parentElement) {
      return [];
    }

    // Create module instances
    const newInstances = MODULES.filter((module) =>
      module.hasOwnProperty("selector")
    ).flatMap((Module) => {
      const blocks = [...parentElement.querySelectorAll(Module.selector)];
      return blocks
        .filter((block) => !block.dataset["instanceIndex" + Module.name])
        .map((block) => {
          const previousIndexes = block.dataset.instanceIndex;
          block.dataset.instanceIndex = previousIndexes
            ? [previousIndexes, this.lastIndex].join(",")
            : this.lastIndex;
          block.dataset["instanceIndex" + Module.name] = this.lastIndex;

          this.lastIndex++;
          return new Module(block);
        });
    });

    // Create modals instances
    Object.keys(MODALS).forEach((k) => {
      const Modal = MODALS[k];

      if (!Modal.hasOwnProperty("selector")) {
        return;
      }

      const block = parentElement.querySelector(Modal.selector);
      if (!block) {
        return;
      }

      if (block.dataset["instanceIndex" + Modal.name]) {
        return;
      }

      const previousIndexes = block.dataset.instanceIndex;
      block.dataset.instanceIndex = previousIndexes
        ? [previousIndexes, this.lastIndex].join(",")
        : this.lastIndex;
      block.dataset["instanceIndex" + Modal.name] = this.lastIndex;
      this.lastIndex++;

      this.modals[k] = new Modal(block);

      newInstances.push(this.modals[k]);
    });

    // Create main module instances
    Object.keys(MODULES).forEach((k) => {
      const Module = MODULES[k];

      if (!Module.hasOwnProperty("selector")) {
        return;
      }

      const block = parentElement.querySelector(Module.selector);

      if (!block) {
        return;
      }

      if (block.dataset["instanceIndex" + Module.name]) {
        return;
      }

      const previousIndexes = block.dataset.instanceIndex;
      block.dataset.instanceIndex = previousIndexes
        ? [previousIndexes, this.lastIndex].join(",")
        : this.lastIndex;
      block.dataset["instanceIndex" + Module.name] = this.lastIndex;
      this.lastIndex++;

      this[k] = new Module(block);

      newInstances.push(this[k]);
    });

    // Call onReady method if any
    newInstances.forEach((instance) => {
      if (instance.onReady) {
        instance.onReady();
      }
    });

    // Call onComplete method if any
    newInstances.forEach((instance) => {
      if (instance.onComplete) {
        instance.onComplete();
      }
    });

    this.instances = this.instances.concat(newInstances);

    return newInstances;
  };

  destroyArea = (parentElement, keep = []) => {
    [...parentElement.children].forEach((element) => {
      // Skip destroy if element is in `keep`
      if (keep.some((s) => element.matches && element.matches(s))) {
        return;
      }

      // If this element has an instance attached
      if (element.dataset.instanceIndex) {
        // Get instance
        const instances = getInstances(element);

        instances.forEach((instance) => {
          // Call onDestroy method if any
          if (instance.onDestroy) {
            instance.onDestroy();
          }

          // Remove instance references
          element.dataset.instanceIndex.split(",").map((i) => {
            this.instances[parseInt(i, 10)] = null;
          });
          Object.keys(MODULES).forEach((k) => {
            if (this[k] === instance) {
              this[k] = null;
            }
          });
          Object.keys(MODALS).forEach((k) => {
            if (this.modals[k] === instance) {
              this.modals[k] = null;
            }
          });
        });
      }

      element.remove();
    });
  };

  onResize = (e) => {
    if (!this.instances) {
      return;
    }

    const currentView = this.getCurrentView();
    const changedView = this.currentView !== currentView;

    this.currentView = currentView;

    this.instances
      .filter((i) => !!i)
      .forEach((instance) => {
        if (instance.onResize) {
          instance.onResize(e, {
            changedView,
          });
        }
      });
  };

  getCurrentView() {
    const isTablet = window.innerWidth >= tablet;
    const isLaptop = window.innerWidth >= laptop;

    if (isLaptop) {
      return "laptop";
    }

    if (isTablet) {
      return "tablet";
    }

    return "mobile";
  }

  onScroll = (e) => {
    if (!this.instances) {
      return;
    }

    this.instances
      .filter((i) => !!i)
      .forEach((instance) => {
        if (instance.onScroll) {
          instance.onScroll(e);
        }
      });
  };

  onRecaptchaReady() {
    this.instances.forEach((instance) => {
      if (instance.hasOwnProperty("onRecaptchaReady")) {
        instance.onRecaptchaReady();
      }
    });
  }
}

window.App = new App();

export const getInstance = (el) => {
  const instanceIndex = el.getAttribute("data-instance-index");

  if (!instanceIndex) {
    return null;
  }

  return window.App.instances[parseInt(instanceIndex, 10)];
};
